import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Popconfirm, App, Space } from 'antd';
import { PlusOutlined, TagsOutlined } from '@ant-design/icons';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { LoadingState, ApiErrorState, EmptyState } from '../../Components/common/StateViews';
import useApiData from '../../Hooks/useApiData';
import cfoService from '../../Services/cfoService';
import { money, formatDateTime } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

const PricingPlans = () => {
  const { message } = App.useApp();
  // Show all plans by default (Mudassir: the plans page lists active + inactive).
  const [includeInactive, setIncludeInactive] = useState(true);
  const { data, loading, error, reload } = useApiData(
    () => cfoService.getPricingPlans(includeInactive),
    [includeInactive]
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [form] = Form.useForm();

  const plans = Array.isArray(data) ? data : [];

  const onToggleActive = async (record, nextActive) => {
    setStatusBusyId(record.id);
    try {
      await cfoService.setPricingPlanStatus(record.id, nextActive);
      message.success(nextActive ? 'Plan reactivated.' : 'Plan deactivated.');
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setStatusBusyId(null);
    }
  };

  const onCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    const payload = { ...values, currency: values.currency?.toUpperCase() };
    setCreating(true);
    try {
      await cfoService.createPricingPlan(payload);
      message.success('Pricing plan created.');
      setModalOpen(false);
      form.resetFields();
      reload();
    } catch (err) {
      const { status, message: msg } = toApiError(err);
      message.error(status === 409 ? msg || 'A plan with that code already exists.' : msg);
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    { title: 'Code', dataIndex: 'code' },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Currency', dataIndex: 'currency' },
    {
      title: 'Monthly / seat',
      dataIndex: 'monthlyRatePerSeat',
      render: (v, r) => money(v, r.currency),
    },
    {
      title: 'Yearly / seat',
      dataIndex: 'yearlyRatePerSeat',
      render: (v, r) => money(v, r.currency),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: (v, r) => (
        <Popconfirm
          title={v ? 'Deactivate this plan? It stops being offered for new customer terms.' : 'Reactivate this plan?'}
          okText="Yes"
          cancelText="No"
          onConfirm={() => onToggleActive(r, !v)}
        >
          <Switch
            checked={v}
            loading={statusBusyId === r.id}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Popconfirm>
      ),
    },
    { title: 'Created', dataIndex: 'createdOn', render: formatDateTime },
  ];

  return (
    <div>
      <WelcomeBanner
        title="Pricing Plans"
        subtitle="Reusable plans applied to customer commercial terms"
        icon={<TagsOutlined />}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Create Pricing Plan
          </Button>
        }
      />

      <Space style={{ marginBottom: 16 }} align="center">
        <Switch checked={includeInactive} onChange={setIncludeInactive} />
        <span>Show inactive plans</span>
      </Space>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : plans.length === 0 ? (
        <EmptyState description="No pricing plans yet." />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={plans} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
      )}

      <Modal
        title="Create Pricing Plan"
        open={modalOpen}
        onOk={onCreate}
        onCancel={() => setModalOpen(false)}
        confirmLoading={creating}
        okText="Create plan"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark="optional" initialValues={{ currency: 'USD' }}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. STARTER" />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Starter" />
          </Form.Item>
          <Form.Item
            name="currency"
            label="Currency"
            rules={[
              { required: true },
              { pattern: /^[A-Za-z]{3}$/, message: 'Use a 3-letter currency code (e.g. USD)' },
            ]}
          >
            <Input placeholder="USD" maxLength={3} />
          </Form.Item>
          <Form.Item name="monthlyRatePerSeat" label="Monthly rate per seat" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="yearlyRatePerSeat" label="Yearly rate per seat" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PricingPlans;
